import React, { useState, useEffect, useMemo } from 'react';
import dbService from '../../../services/dbService';
import { Form, Card, Table, Spinner } from 'react-bootstrap';
import PageContainer from '../../layout/PageContainer';
import DatePicker from 'react-datepicker';

const TransactionAnalysis = () => {
    const [transactions, setTransactions] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // --- State for filters ---
    const [selectedCustomerId, setSelectedCustomerId] = useState('All');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [minAmount, setMinAmount] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const [txnData, custData] = await Promise.all([
                dbService.getAll('transactions'),
                dbService.getAll('customers')
            ]);
            setTransactions(txnData);
            setCustomers(custData);
            setIsLoading(false);
        };
        fetchData();
    }, []);

    const filteredTransactions = useMemo(() => {
        const start = startDate ? startDate.toISOString().split('T')[0] : null;
        const end = endDate ? endDate.toISOString().split('T')[0] : null;

        return transactions
            .filter(t => selectedCustomerId === 'All' || t.customerId === selectedCustomerId)
            .filter(t => !start || t.date >= start)
            .filter(t => !end || t.date <= end)
            .filter(t => !minAmount || (t.amount && t.amount >= parseInt(minAmount, 10)))
            .reverse();
    }, [transactions, selectedCustomerId, startDate, endDate, minAmount]);

    if (isLoading) {
        return (
            <PageContainer title="Transaction Analysis">
                <div className="text-center p-5"><Spinner animation="border" /></div>
            </PageContainer>
        );
    }

    return (
        <PageContainer title="Transaction Analysis">
            <p>Perform ad-hoc analysis on the complete historical transaction data lake. Use the filters below to investigate specific patterns.</p>
            <Card className="mb-4">
                <Card.Header>Filters</Card.Header>
                <Card.Body>
                    <Form className="row g-3">
                        <div className="col-md-3">
                            <Form.Label>Customer</Form.Label>
                            <Form.Select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
                                <option value="All">All Customers</option>
                                {customers.map(c => <option key={c.id} value={c.id}>{c.fullName}</option>)}
                            </Form.Select>
                        </div>
                        <div className="col-md-3">
                            <Form.Label>Start Date</Form.Label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                isClearable
                                placeholderText="Select start date"
                                className="form-control"
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label>End Date</Form.Label>
                             <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                isClearable
                                placeholderText="Select end date"
                                className="form-control"
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label>Minimum Amount ($)</Form.Label>
                            <Form.Control type="number" name="minAmount" placeholder="e.g. 5000" value={minAmount} onChange={e => setMinAmount(e.target.value)} />
                        </div>
                    </Form>
                </Card.Body>
            </Card>
            <Card>
                <Card.Body>
                    <div className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr><th>Date</th><th>Customer</th><th>Type</th><th>Amount</th><th>Counterparty Country</th></tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.slice(0, 100).map(t => (
                                    <tr key={t.id}>
                                        <td>{t.date}</td>
                                        <td>{t.customerName}</td>
                                        <td>{t.type}</td>
                                        
                                        {/* --- THIS IS THE CRITICAL FIX --- */}
                                        {/* Use optional chaining (?.) to prevent crash on undefined amount */}
                                        <td>${t.amount?.toLocaleString() || 'N/A'}</td>
                                        {/* --- END OF CRITICAL FIX --- */}

                                        <td>{t.counterpartyCountry}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {filteredTransactions.length === 0 && <p className="text-center text-muted p-4">No transactions match the current filters.</p>}
                        {filteredTransactions.length > 100 && <p className="text-muted text-center mt-3">Showing first 100 of {filteredTransactions.length} results.</p>}
                    </div>
                </Card.Body>
            </Card>
        </PageContainer>
    );
};

export default TransactionAnalysis;